import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Alert,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Print as PrintIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  SaveAlt as SaveAltIcon
} from '@mui/icons-material';
import JsBarcode from 'jsbarcode';
import {
  getInvoiceByNumber,
  getInvoiceProductsForBarcodes,
  saveBarcodeSettings,
  getBarcodeSettings
} from '../../services/barcodeService';
import {
  Invoice,
  Product,
  ProductVariant,
  PrintSettings
} from '../../types/barcode';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import { usePermissionsContext } from '../../context/PermissionContext';

// الصفحة الرئيسية لطباعة باركود المنتجات
const ProductBarcodePage: React.FC = () => {
  // حالة البيانات
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState<Invoice | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // صلاحيات المستخدم
  const { hasInvoiceSettingPermission } = useInvoicePermissions();
  const { loading: permissionsLoading } = usePermissionsContext();
  const canSaveBarcodeSettings = hasInvoiceSettingPermission('product-barcode', 'save_print_settings');

  // حالة إعدادات الطباعة
  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    paperWidth: 50,
    paperHeight: 30,
    marginTop: 5,
    marginRight: 5,
    marginBottom: 5,
    marginLeft: 5,
    labelsPerRow: 2,
    showProductName: true,
    showVariantAttributes: true,
    showBarcode: true,
    showPrice: true,
    showCompanyName: true,
    showInvoiceNumber: true,
    barcodeWidth: 2,
    barcodeHeight: 50,
    fontSize: 10,
    companyName: 'اسم الشركة',
    selectedPriceType: 'retailPrice'
  });

  const printContainerRef = useRef<HTMLDivElement>(null);
  const [settingsExpanded, setSettingsExpanded] = useState<boolean>(true);

  // جلب إعدادات الطباعة عند تحميل الصفحة
  useEffect(() => {
    fetchPrintSettings();
  }, []);

  // إغلاق قسم الإعدادات إذا لم تكن هناك صلاحية للحفظ
  useEffect(() => {
    if (!permissionsLoading && !canSaveBarcodeSettings) {
      setSettingsExpanded(false);
    }
  }, [canSaveBarcodeSettings, permissionsLoading]);

  // جلب إعدادات الطباعة
  const fetchPrintSettings = async () => {
    try {
      const response = await getBarcodeSettings();
      if (response.success) {
        setPrintSettings(response.settings);
      }
    } catch (error) {
      console.error('Error fetching print settings:', error);
      // استخدام الإعدادات الافتراضية المحددة مسبقاً
    }
  };

  // البحث عن الفاتورة وجلب منتجاتها
  const handleSearchInvoice = async () => {
    if (!invoiceNumber.trim()) {
      setAlert({ type: 'error', message: 'الرجاء إدخال رقم الفاتورة' });
      return;
    }

    try {
      setLoading(true);
      setProducts([]);
      setSelectedInvoiceData(null);

      // البحث عن الفاتورة برقمها
      const invoiceResponse = await getInvoiceByNumber(invoiceNumber.trim());
      
      if (!invoiceResponse.success || !invoiceResponse.invoice) {
        setAlert({ type: 'error', message: 'الفاتورة غير موجودة أو ليست من نوع مشتريات أو عرض سعر' });
        setLoading(false);
        return;
      }

      setSelectedInvoiceData(invoiceResponse.invoice);

      // جلب منتجات الفاتورة
      const productsResponse = await getInvoiceProductsForBarcodes(invoiceResponse.invoice._id);
      
      if (productsResponse.success) {
        setProducts(productsResponse.products || []);
        if (productsResponse.products && productsResponse.products.length > 0) {
          setAlert({ type: 'success', message: `تم جلب ${productsResponse.products.length} منتج من الفاتورة بنجاح` });
        } else {
          setAlert({ type: 'info', message: 'لا توجد منتجات في هذه الفاتورة' });
        }
      } else {
        setAlert({ type: 'error', message: 'فشل في جلب منتجات الفاتورة' });
        setProducts([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error searching invoice:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء البحث عن الفاتورة' });
      setLoading(false);
      setProducts([]);
      setSelectedInvoiceData(null);
    }
  };

  // تحديث حالة المنتجات عند اختيار أو إلغاء اختيار متغير
  const handleVariantSelection = (productIndex: number, variantIndex: number) => {
    const updatedProducts = [...products];
    const variant = updatedProducts[productIndex].variants[variantIndex];
    variant.selected = !variant.selected;
    setProducts(updatedProducts);
  };

  // الحصول على السعر المناسب حسب النوع المختار
  const getVariantPrice = (variant: any) => {
    const priceType = printSettings.selectedPriceType;
    switch (priceType) {
      case 'wholesalePrice':
        return variant.wholesalePrice || 0;
      case 'customPrice1':
        return variant.customPrice1 || 0;
      case 'customPrice2':
        return variant.customPrice2 || 0;
      case 'retailPrice':
      default:
        return variant.price || 0;
    }
  };


  // تحديث إعدادات الطباعة
  const handlePrintSettingChange = (field: keyof PrintSettings, value: number | boolean | string) => {
    setPrintSettings(prev => ({ ...prev, [field]: value }));
  };

  // حفظ إعدادات الطباعة في الخادم
  const handleSaveSettings = async () => {
    try {
      const response = await saveBarcodeSettings(printSettings);
      if (response.success) {
        setAlert({ type: 'success', message: 'تم حفظ الإعدادات بنجاح' });
      } else {
        setAlert({ type: 'error', message: 'فشل في حفظ الإعدادات' });
      }
    } catch (error) {
      console.error('Error saving print settings:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء حفظ الإعدادات' });
    }
  };

  // تحديد كل المتغيرات
  const selectAllVariants = () => {
    const updatedProducts = products.map(product => ({
      ...product,
      variants: product.variants.map(variant => ({ ...variant, selected: true }))
    }));
    setProducts(updatedProducts);
  };

  // إلغاء تحديد كل المتغيرات
  const deselectAllVariants = () => {
    const updatedProducts = products.map(product => ({
      ...product,
      variants: product.variants.map(variant => ({ ...variant, selected: false }))
    }));
    setProducts(updatedProducts);
  };

  // إنشاء الباركود
  useEffect(() => {
    // إنشاء باركود لكل متغير منتج
    products.forEach(product => {
      product.variants.forEach(variant => {
        if (variant.barcode) {
          try {
            const barcodeElement = document.getElementById(`barcode-${variant._id}`);
            if (barcodeElement) {
              JsBarcode(barcodeElement, variant.barcode, {
                width: printSettings.barcodeWidth,
                height: printSettings.barcodeHeight,
                displayValue: true,
                fontSize: printSettings.fontSize,
                margin: 0
              });
            }
          } catch (error) {
            console.error('Error generating barcode:', error);
          }
        }
      });
    });
  }, [products, printSettings.barcodeWidth, printSettings.barcodeHeight, printSettings.fontSize]);

  // طباعة الباركود
  const handlePrint = () => {
    const selectedVariantsCount = products.reduce((count, product) => 
      count + product.variants.filter(v => v.selected).length, 0);
    
    if (selectedVariantsCount === 0) {
      setAlert({ type: 'error', message: 'الرجاء اختيار متغير واحد على الأقل للطباعة' });
      return;
    }

    // إنشاء صفحة الطباعة
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setAlert({ type: 'error', message: 'تم منع النافذة المنبثقة. الرجاء السماح بالنوافذ المنبثقة وإعادة المحاولة.' });
      return;
    }

    // إضافة أنماط CSS للطباعة
    const selectedInvoiceObj = selectedInvoiceData;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>طباعة باركود المنتجات - ${selectedInvoiceObj?.invoiceNumber || ''}</title>
        <style>
          @page {
            size: ${printSettings.paperWidth}mm ${printSettings.paperHeight}mm !important;
            margin: ${printSettings.marginTop}mm ${printSettings.marginRight}mm ${printSettings.marginBottom}mm ${printSettings.marginLeft}mm !important;
          }
          @media print {
            html, body {
              width: ${printSettings.paperWidth}mm !important;
              height: ${printSettings.paperHeight}mm !important;
            }
          }
          body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 0;
            width: ${printSettings.paperWidth}mm;
            height: ${printSettings.paperHeight}mm;
            direction: rtl;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .label-container {
            display: grid;
            grid-template-columns: repeat(${printSettings.labelsPerRow}, 1fr);
            gap: 5mm;
            width: 100%;
            box-sizing: border-box;
          }
          .label {
            border: 1px dashed #ccc;
            padding: 2mm;
            text-align: center;
            break-inside: avoid;
            page-break-inside: avoid;
            box-sizing: border-box;
          }
          .product-name {
            font-weight: bold;
            font-size: ${printSettings.fontSize}pt;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 2mm;
            display: ${printSettings.showProductName ? 'block' : 'none'};
          }
          .variant-attributes {
            font-size: ${printSettings.fontSize - 1}pt;
            margin-bottom: 2mm;
            display: ${printSettings.showVariantAttributes ? 'block' : 'none'};
          }
          .barcode-container {
            margin: 2mm 0;
            display: ${printSettings.showBarcode ? 'block' : 'none'};
          }
          .price {
            font-weight: bold;
            font-size: ${printSettings.fontSize}pt;
            margin-top: 2mm;
            display: ${printSettings.showPrice ? 'block' : 'none'};
          }
          .company-name {
            font-size: ${printSettings.fontSize - 2}pt;
            margin-top: 2mm;
            display: ${printSettings.showCompanyName ? 'block' : 'none'};
          }
          .invoice-number {
            font-size: ${printSettings.fontSize - 2}pt;
            margin-top: 1mm;
            display: ${printSettings.showInvoiceNumber ? 'block' : 'none'};
          }
        </style>
      </head>
      <body>
        <div class="label-container">
    `);

    // حساب إجمالي عدد الباركودات التي سيتم طباعتها (الكمية)
    let totalBarcodesToPrint = 0;
    let barcodeElements: string[] = [];
    
    products.forEach(product => {
      product.variants
        .filter(variant => variant.selected)
        .forEach(variant => {
          // طباعة الباركود بعدد مرات مساوٍ للكمية في الفاتورة
          const quantity = variant.quantity || 1;
          
          for (let i = 0; i < quantity; i++) {
            totalBarcodesToPrint++;
            barcodeElements.push(`
              <div class="label">
                <div class="product-name">${product.name}</div>
                <div class="variant-attributes">
                  ${Object.entries(variant.attributes)
                    .filter(([key]) => key !== 'barcode')
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(' | ')}
                </div>
                <div class="barcode-container">
                  <svg id="print-barcode-${variant._id}-${i}"></svg>
                </div>
                <div class="price">${getVariantPrice(variant)} ريال</div>
                <div class="company-name">${printSettings.companyName}</div>
                <div class="invoice-number">PO: ${invoiceNumber || ''}</div>
              </div>
            `);
          }
        });
    });
    
    // طباعة الباركودات
    barcodeElements.forEach(element => {
      printWindow.document.write(element);
    });

    printWindow.document.write(`
        </div>
        <script src="/js/JsBarcode.all.min.js"></script>
        <script>
          // إنشاء الباركود بعد تحميل الصفحة
          window.onload = function() {
    `);

    // إضافة كود JavaScript لإنشاء الباركود لكل نسخة حسب الكمية
    let barcodeScripts: string[] = [];
    products.forEach(product => {
      product.variants
        .filter(variant => variant.selected && variant.barcode)
        .forEach(variant => {
          const quantity = variant.quantity || 1;
          
          for (let i = 0; i < quantity; i++) {
            barcodeScripts.push(`
              if (document.getElementById('print-barcode-${variant._id}-${i}')) {
                JsBarcode('#print-barcode-${variant._id}-${i}', '${variant.barcode}', {
                  width: ${printSettings.barcodeWidth},
                  height: ${printSettings.barcodeHeight},
                  displayValue: true,
                  fontSize: ${printSettings.fontSize},
                  margin: 0
                });
              }
            `);
          }
        });
    });
    
    // إضافة جميع سكريبتات الباركود
    barcodeScripts.forEach(script => {
      printWindow.document.write(script);
    });

    printWindow.document.write(`
            // عرض رسالة بعدد الباركودات التي ستتم طباعتها
            console.log("سيتم طباعة ${totalBarcodesToPrint} باركود حسب الكميات في الفاتورة");
            
            // طباعة بعد إنشاء كل الباركودات
            setTimeout(function() {
              window.print();
              // إعادة تنفيذ الطباعة بعد فترة إذا كان عدد الصفحات أكثر من المتوقع
              setTimeout(function() {
                // هذا سيمنع ظهور صفحات فارغة إضافية
                window.focus();
              }, 500);
            }, 1000);
          };
        </script>
      </body>
      </html>
    `);

    printWindow.document.close();
    setAlert({ type: 'success', message: 'تمت تهيئة صفحة الطباعة بنجاح' });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        طباعة باركود المنتجات
      </Typography>

      {alert && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* القسم الرئيسي - اختيار الفاتورة والمنتجات */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              البحث عن الفاتورة
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="رقم الفاتورة"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchInvoice();
                  }
                }}
                disabled={loading}
                placeholder="أدخل رقم الفاتورة (مشتريات أو عرض سعر فقط)"
                helperText="يمكنك البحث عن فواتير المشتريات وعروض الأسعار فقط"
              />
              <Button
                variant="contained"
                onClick={handleSearchInvoice}
                disabled={loading || !invoiceNumber.trim()}
                sx={{ minWidth: 120 }}
              >
                بحث
              </Button>
            </Box>

            {selectedInvoiceData && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>الفاتورة:</strong> {selectedInvoiceData.invoiceNumber} |
                <strong> العميل:</strong> {selectedInvoiceData.customer?.name || 'بدون عميل'} |
                <strong> التاريخ:</strong> {new Date(selectedInvoiceData.date).toLocaleDateString('ar-SA')}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {selectedInvoiceData && products.length > 0 ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        منتجات الفاتورة
                      </Typography>
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={selectAllVariants}
                          sx={{ mr: 1 }}
                        >
                          تحديد الكل
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={deselectAllVariants}
                          sx={{ mr: 1 }}
                        >
                          إلغاء تحديد الكل
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handlePrint}
                          disabled={products.reduce((count, product) =>
                            count + product.variants.filter(v => v.selected).length, 0) === 0}
                        >
                          طباعة الباركود ({products.reduce((total, product) =>
                            total + product.variants
                              .filter(v => v.selected)
                              .reduce((subtotal, v) => subtotal + (v.quantity || 1), 0), 0)} قطعة)
                        </Button>
                      </Box>
                    </Box>

                    {products.map((product, productIndex) => (
                      <Card key={product._id} sx={{ mb: 2, border: '1px solid #eee' }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {product.name} {product.code ? `(${product.code})` : ''}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          
                          <Grid container spacing={2}>
                            {product.variants.map((variant, variantIndex) => (
                              <Grid item xs={12} sm={6} key={variant._id}>
                                <Box
                                  sx={{
                                    border: '1px solid #ddd',
                                    borderRadius: 1,
                                    p: 2,
                                    backgroundColor: variant.selected ? 'rgba(0, 200, 83, 0.05)' : 'transparent',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={variant.selected || false}
                                        onChange={() => handleVariantSelection(productIndex, variantIndex)}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                          {product.name}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>الخصائص:</strong> {Object.entries(variant.attributes)
                                            .filter(([key]) => key !== 'barcode')
                                            .map(([key, value]) => `${key}: ${value}`)
                                            .join(' | ')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          <strong>السعر:</strong> {getVariantPrice(variant)} ريال
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          <strong>الكمية بالفاتورة:</strong> {variant.quantity || 1} قطعة
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                          <strong>الباركود:</strong> {variant.barcode || 'غير متوفر'}
                                        </Typography>
                                        {variant.barcode && (
                                          <Box sx={{ mt: 1, textAlign: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                              سيتم طباعة {variant.quantity || 1} باركود
                                            </Typography>
                                            <svg id={`barcode-${variant._id}`}></svg>
                                          </Box>
                                        )}
                                      </Box>
                                    }
                                    sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </>
                ) : (
                  selectedInvoiceData && (
                    <Alert severity="info">
                      لا توجد منتجات في هذه الفاتورة أو لم يتم العثور على منتجات ذات متغيرات.
                    </Alert>
                  )
                )}
              </>
            )}
          </Paper>

        </Grid>

        {/* قسم الإعدادات */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} /> إعدادات الطباعة
            </Typography>

            <Accordion expanded={settingsExpanded} onChange={(e, expanded) => setSettingsExpanded(expanded)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>حجم الورق والهوامش</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="عرض الورق (مم)"
                      type="number"
                      value={printSettings.paperWidth}
                      onChange={(e) => handlePrintSettingChange('paperWidth', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 20, max: 210 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="ارتفاع الورق (مم)"
                      type="number"
                      value={printSettings.paperHeight}
                      onChange={(e) => handlePrintSettingChange('paperHeight', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 20, max: 297 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="هامش علوي (مم)"
                      type="number"
                      value={printSettings.marginTop}
                      onChange={(e) => handlePrintSettingChange('marginTop', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 50 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="هامش سفلي (مم)"
                      type="number"
                      value={printSettings.marginBottom}
                      onChange={(e) => handlePrintSettingChange('marginBottom', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 50 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="هامش يمين (مم)"
                      type="number"
                      value={printSettings.marginRight}
                      onChange={(e) => handlePrintSettingChange('marginRight', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 50 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="هامش يسار (مم)"
                      type="number"
                      value={printSettings.marginLeft}
                      onChange={(e) => handlePrintSettingChange('marginLeft', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 50 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="عدد الملصقات في الصف"
                      type="number"
                      value={printSettings.labelsPerRow}
                      onChange={(e) => handlePrintSettingChange('labelsPerRow', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 1, max: 10 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disabled={!canSaveBarcodeSettings}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>إعدادات الباركود</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="عرض الباركود"
                      type="number"
                      value={printSettings.barcodeWidth}
                      onChange={(e) => handlePrintSettingChange('barcodeWidth', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 1, max: 5, step: 0.5 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="ارتفاع الباركود"
                      type="number"
                      value={printSettings.barcodeHeight}
                      onChange={(e) => handlePrintSettingChange('barcodeHeight', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 10, max: 100 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="حجم الخط"
                      type="number"
                      value={printSettings.fontSize}
                      onChange={(e) => handlePrintSettingChange('fontSize', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 6, max: 20 } }}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="اسم الشركة"
                      value={printSettings.companyName}
                      onChange={(e) => handlePrintSettingChange('companyName', e.target.value)}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={!canSaveBarcodeSettings}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion disabled={!canSaveBarcodeSettings}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>السعر</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth size="small" margin="dense" disabled={!canSaveBarcodeSettings}>
                  <InputLabel>نوع السعر المعروض</InputLabel>
                  <Select
                    value={printSettings.selectedPriceType}
                    label="نوع السعر المعروض"
                    onChange={(e) => handlePrintSettingChange('selectedPriceType', e.target.value)}
                  >
                    <MenuItem value="retailPrice">سعر التجزئة</MenuItem>
                    <MenuItem value="wholesalePrice">سعر الجملة</MenuItem>
                    <MenuItem value="customPrice1">السعر المخصص 1</MenuItem>
                    <MenuItem value="customPrice2">السعر المخصص 2</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Accordion disabled={!canSaveBarcodeSettings}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>العناصر المعروضة</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showProductName}
                        onChange={(e) => handlePrintSettingChange('showProductName', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار اسم المنتج"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showVariantAttributes}
                        onChange={(e) => handlePrintSettingChange('showVariantAttributes', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار خصائص المتغير"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showBarcode}
                        onChange={(e) => handlePrintSettingChange('showBarcode', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار صورة الباركود"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showPrice}
                        onChange={(e) => handlePrintSettingChange('showPrice', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار السعر"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showCompanyName}
                        onChange={(e) => handlePrintSettingChange('showCompanyName', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار اسم الشركة"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printSettings.showInvoiceNumber}
                        onChange={(e) => handlePrintSettingChange('showInvoiceNumber', e.target.checked)}
                        disabled={!canSaveBarcodeSettings}
                      />
                    }
                    label="إظهار رقم الفاتورة"
                  />
                </FormGroup>
              </AccordionDetails>
            </Accordion>

            {/* معلومات حول الطابعة */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.main', color: 'white', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                معلومات الطابعة
              </Typography>
              <Typography variant="body2">
                نوع الطابعة: Zebra 2844
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>ميزة جديدة:</strong> يتم طباعة الباركود حسب الكمية الموجودة في الفاتورة! إذا كان المنتج بكمية 5، سيتم طباعة 5 باركودات.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                تلميح: عند الطباعة، اختر "حجم ورق مخصص" من نافذة الطباعة واضبطه على {printSettings.paperWidth}مم × {printSettings.paperHeight}مم.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ملاحظة: بعض المتصفحات قد تستخدم حجم A4 افتراضيًا بغض النظر عن الإعدادات. في هذه الحالة، يجب ضبط الحجم يدويًا من نافذة الطباعة.
              </Typography>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<SaveAltIcon />}
                onClick={handleSaveSettings}
                disabled={!canSaveBarcodeSettings}
              >
                حفظ الإعدادات
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setPrintSettings({
                  paperWidth: 50,
                  paperHeight: 30,
                  marginTop: 5,
                  marginRight: 5,
                  marginBottom: 5,
                  marginLeft: 5,
                  labelsPerRow: 2,
                  showProductName: true,
                  showVariantAttributes: true,
                  showBarcode: true,
                  showPrice: true,
                  showCompanyName: true,
                  showInvoiceNumber: true,
                  barcodeWidth: 2,
                  barcodeHeight: 50,
                  fontSize: 10,
                  companyName: 'اسم الشركة',
                  selectedPriceType: 'retailPrice'
                })}
                disabled={!canSaveBarcodeSettings}
              >
                إعادة تعيين
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductBarcodePage;